import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { MediaDevices, MediaDevicesEvents } from "../../utils/media-devices-utils";
import PropTypes from "prop-types";

const CameraDevicesContext = createContext();

export function CameraDevicesProvider({ children }) {
  const mediaDevicesManager = APP.mediaDevicesManager;
  const mediaDevicesManagerPermission = APP.mediaDevicesManagerPermissions;

  const [videoDevices, setVideoDevices] = useState({
    value: mediaDevicesManager.selectedVideoDeviceId,
    options: mediaDevicesManager.videoDevicesOptions
  });

  const [permissionStatusVideo, setPermissionsStatusVideo] = useState(
    mediaDevicesManager.getPermissionsStatus(MediaDevices.CAMERA)
  );

  const [requestingVideoPermission, setRequestVideoPermission] = useState(false);

  // Handle permissions status changes
  useEffect(() => {
    let isMounted = true;

    const onPermissionsChanged = ({ mediaDevice, status }) => {
      if (mediaDevice === MediaDevices.CAMERA && isMounted) {
        setPermissionsStatusVideo(status);
      }
    };

    mediaDevicesManager.on(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, onPermissionsChanged);

    return () => {
      isMounted = false;
      mediaDevicesManager.off(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, onPermissionsChanged);
    };
  }, [setPermissionsStatusVideo, mediaDevicesManager]);

  // Handle video device change
  const videoDeviceChanged = useCallback(
    async deviceId => {
      await mediaDevicesManager.changeVideoDevice(deviceId);
      setVideoDevices({
        value: mediaDevicesManager.selectedVideoDeviceId,
        options: mediaDevicesManager.videoDevicesOptions
      });
    },
    [mediaDevicesManager]
  );

  // Fetch video devices with permission handling
  const fetchVideoDevices = useCallback(async () => {
    setRequestVideoPermission(true);
    await mediaDevicesManagerPermission.fetchVideoDevices();

    setVideoDevices({
      value: mediaDevicesManager.selectedVideoDeviceId,
      options: mediaDevicesManager.videoDevicesOptions
    });
    setRequestVideoPermission(false);
  }, [
    mediaDevicesManagerPermission,
    mediaDevicesManager.selectedVideoDeviceId,
    mediaDevicesManager.videoDevicesOptions
  ]);

  // Update video devices on changes
  const videoDevicesChanged = useCallback(() => {
    setVideoDevices({
      value: mediaDevicesManager.selectedVideoDeviceId,
      options: mediaDevicesManager.videoDevicesOptions
    });
  }, [mediaDevicesManager.selectedVideoDeviceId, mediaDevicesManager.videoDevicesOptions]);

  // Fetch video devices
  const getVideoDevices = useCallback(async () => {
    await mediaDevicesManager.fetchVideoDevices();

    if (mediaDevicesManager.selectedVideoDeviceId !== null) {
      setVideoDevices({
        value: mediaDevicesManager.selectedVideoDeviceId,
        options: mediaDevicesManager.videoDevicesOptions
      });
    }
  }, [mediaDevicesManager]);

  // Subscribe to VIDEO_DEVICES_CHANGED events
  useEffect(() => {
    mediaDevicesManager.on(MediaDevicesEvents.VIDEO_DEVICES_CHANGED, videoDevicesChanged);

    return () => {
      mediaDevicesManager.off(MediaDevicesEvents.VIDEO_DEVICES_CHANGED, videoDevicesChanged);
    };
  }, [videoDevicesChanged, mediaDevicesManager]);

  // Context value
  const value = {
    fetchVideoDevices,
    videoDeviceChanged,
    getVideoDevices,
    videoDevices,
    requestingVideoPermission,
    permissionStatusVideo
  };

  return <CameraDevicesContext.Provider value={value}>{children}</CameraDevicesContext.Provider>;
}

// Custom hook to use the context
export function useCameraDevices() {
  const context = useContext(CameraDevicesContext);
  if (!context) {
    throw new Error("useCameraDevices must be used within a CameraDevicesProvider");
  }
  return context;
}

// Prop types
CameraDevicesProvider.propTypes = {
  children: PropTypes.node
};
